import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  BrainCircuit,
  CheckCircle2,
  CreditCard,
  Lock,
  Mail,
  Save,
  Send,
  ShieldCheck,
} from 'lucide-react';
import { api, ApiError } from '@/lib/api';
import { Card, CardBody, CardHeader } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Badge } from '@/components/ui/Badge';
import { Field, Input, Select, Toggle } from '@/components/ui/Input';
import { PageLoader } from '@/components/ui/States';
import { useToast } from '@/components/ui/Toast';
import { useAuthStore } from '@/store/auth';
import { cn } from '@/lib/cn';

/**
 * Pengaturan operasional situs.
 *
 * Halaman ini ada supaya Anda tidak perlu masuk ke server dan menyunting
 * berkas `.env` setiap kali mengganti kata sandi SMTP atau kunci Duitku. Yang
 * disimpan di sini menimpa isi `.env`, jadi apa yang tampak di layar memang
 * yang sedang dipakai layanan.
 *
 * Nilai rahasia tidak pernah dikirim balik ke peramban. Kotak isiannya selalu
 * tampil kosong, dan membiarkannya kosong berarti "jangan diubah", bukan
 * "hapus". Untuk menghapusnya, ganti dengan nilai baru.
 */

/** Penanda terisi atau belum, tanpa membocorkan isinya. */
const Jejak = ({ terisi, jejak }) =>
  terisi ? (
    <Badge tone="success" size="sm">
      Terisi {jejak}
    </Badge>
  ) : (
    <Badge tone="warning" size="sm">
      Belum diisi
    </Badge>
  );

const Bagian = ({ icon: Icon, judul, deskripsi, badge, kunciRoot, bolehUbah, children }) => (
  <Card>
    <CardHeader
      title={
        <span className="flex flex-wrap items-center gap-2">
          <Icon className="size-[18px] text-[var(--text-secondary)]" aria-hidden="true" />
          {judul}
          {badge}
        </span>
      }
      description={deskripsi}
    />
    <CardBody className="space-y-4">
      {kunciRoot && !bolehUbah && (
        <p className="flex items-start gap-2 rounded-lg bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-secondary)]">
          <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          Bagian ini menyimpan kredensial, jadi hanya pemilik (root) yang boleh mengubahnya. Anda
          tetap bisa melihat statusnya dan pengaturan yang tidak rahasia.
        </p>
      )}
      {children}
    </CardBody>
  </Card>
);

// -- Email keluar ---------------------------------------------------------

const BagianSmtp = ({ data, bolehUbah, onSimpan, menyimpan }) => {
  const [f, setF] = useState({
    host: data.host ?? '',
    port: String(data.port ?? 587),
    user: data.user ?? '',
    from: data.from ?? '',
    notifikasiKe: data.notifikasiKe ?? '',
    pass: '',
  });
  const [tujuanUji, setTujuanUji] = useState('');
  const [hasilUji, setHasilUji] = useState(null);
  const toast = useToast();

  // Hasilnya ditampilkan menetap di halaman, bukan sebagai toast. Pesan
  // kegagalan SMTP sekarang berupa paragraf berisi langkah yang harus
  // dikerjakan, dan paragraf yang menghilang sendiri dalam empat detik tidak
  // ada gunanya bagi orang yang sedang membetulkan pengaturannya.
  const uji = useMutation({
    mutationFn: (ke) => api.post('/platform/settings/smtp/test', { ke }),
    onSuccess: (res) => {
      setHasilUji(res?.data ?? null);
      if (res?.data?.berhasil) toast.success('Terkirim', { description: res.data.pesan });
    },
    onError: (err) => {
      const pesan = err instanceof ApiError ? err.message : 'Coba lagi sebentar lagi.';
      setHasilUji({ berhasil: false, tahap: 'permintaan', pesan });
      toast.error('Gagal menguji', { description: pesan });
    },
  });

  const ubah = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  return (
    <Bagian
      icon={Mail}
      judul="Email keluar (SMTP)"
      deskripsi="Dipakai untuk email verifikasi akun dan pemulihan kata sandi. Selama belum bisa mengirim, verifikasi email tidak menghalangi siapa pun mendaftar."
      badge={
        <Badge
          tone={data.siap ? 'success' : data.terbukti === false ? 'danger' : 'warning'}
          size="sm"
        >
          {data.siap ? 'Aktif' : data.terbukti === false ? 'Ditolak server' : 'Belum aktif'}
        </Badge>
      }
      kunciRoot
      bolehUbah={bolehUbah}
    >
      {data.terisi && data.terbukti === false && (
        <p className="flex items-start gap-2 rounded-lg bg-danger-50 p-3 text-xs leading-relaxed text-danger-700 dark:bg-danger-500/10 dark:text-danger-500">
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
          <span>
            <strong className="block">Pengaturannya terisi, tetapi ditolak server email.</strong>
            Selama ini belum dibetulkan, tidak ada satu pun email yang bisa sampai ke pengguna,
            jadi penjagaan verifikasi email sengaja dimatikan supaya tidak ada yang terkunci di
            luar akunnya sendiri. Untuk sebagian besar penyedia, nama pengguna adalah alamat
            email lengkapnya, dan kata sandinya harus disetel dari panel penyedia lebih dulu.
          </span>
        </p>
      )}

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Host" hint="Contoh: smtp.gmail.com">
          {(p) => <Input {...p} value={f.host} onChange={ubah('host')} disabled={!bolehUbah} />}
        </Field>
        <Field label="Port" hint="587 untuk STARTTLS, 465 untuk TLS langsung">
          {(p) => (
            <Input
              {...p}
              type="number"
              value={f.port}
              onChange={ubah('port')}
              disabled={!bolehUbah}
            />
          )}
        </Field>
        {/* Peringatan, bukan penolakan. Ada penyedia yang memang memakai nama
            pengguna tanpa @, jadi menolaknya akan salah untuk sebagian orang.
            Yang dibutuhkan cuma supaya kesalahan paling sering ini tertangkap
            sebelum disimpan, bukan sesudah berjam-jam menebak kata sandi yang
            sebenarnya sudah benar. */}
        <Field
          label="Pengguna"
          hint="Alamat email lengkapnya, misalnya admin@crawel.com. Bukan bagian sebelum tanda @."
          error={
            f.user && !f.user.includes('@')
              ? 'Tidak memuat tanda @. Sebagian besar penyedia menolak bentuk ini.'
              : undefined
          }
        >
          {(p) => <Input {...p} value={f.user} onChange={ubah('user')} disabled={!bolehUbah} />}
        </Field>
        <Field label="Alamat pengirim" hint="Contoh: Crawel <halo@crawel.com>">
          {(p) => <Input {...p} value={f.from} onChange={ubah('from')} disabled={!bolehUbah} />}
        </Field>
        <Field
          label="Kotak surat tim"
          hint="Ke mana laporan bug dan balasan pengguna dikirim. Kosong berarti memakai alamat pengirim."
        >
          {(p) => (
            <Input
              {...p}
              type="email"
              placeholder="admin@crawel.com"
              value={f.notifikasiKe}
              onChange={ubah('notifikasiKe')}
              disabled={!bolehUbah}
            />
          )}
        </Field>
      </div>

      <Field
        label={
          <span className="flex flex-wrap items-center gap-2">
            Kata sandi <Jejak terisi={data.passTerisi} jejak={data.passJejak} />
          </span>
        }
        hint="Biarkan kosong kalau tidak ingin menggantinya. Untuk Gmail, pakai App Password, bukan kata sandi akunnya."
      >
        {(p) => (
          <Input
            {...p}
            type="password"
            autoComplete="new-password"
            placeholder={data.passTerisi ? 'Tersimpan, biarkan kosong' : 'Belum diisi'}
            value={f.pass}
            onChange={ubah('pass')}
            disabled={!bolehUbah}
          />
        )}
      </Field>

      <Button
        onClick={() => onSimpan('smtp', { ...f, port: Number(f.port) })}
        loading={menyimpan}
        disabled={!bolehUbah}
        icon={Save}
      >
        Simpan pengaturan email
      </Button>

      <div className="rounded-lg border border-[var(--border-subtle)] p-4">
        <p className="text-sm font-semibold text-[var(--text-primary)]">Coba kirim sekarang</p>
        <p className="mt-1 text-xs text-[var(--text-secondary)]">
          Kesalahan SMTP biasanya baru ketahuan saat ada orang mendaftar dan emailnya tidak pernah
          sampai. Kirim satu email percobaan supaya Anda tahu sekarang, bukan nanti.
        </p>
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <Input
            type="email"
            placeholder="alamat@tujuan.com"
            value={tujuanUji}
            onChange={(e) => setTujuanUji(e.target.value)}
            className="max-w-xs"
            disabled={!bolehUbah}
          />
          <Button
            variant="secondary"
            icon={Send}
            loading={uji.isPending}
            disabled={!bolehUbah || !tujuanUji}
            onClick={() => uji.mutate(tujuanUji)}
          >
            Kirim percobaan
          </Button>
        </div>

        {hasilUji && (
          <div
            className={cn(
              'mt-3 flex items-start gap-2 rounded-lg p-3 text-xs leading-relaxed',
              hasilUji.berhasil
                ? 'bg-success-50 text-success-700 dark:bg-success-500/10 dark:text-success-500'
                : 'bg-danger-50 text-danger-700 dark:bg-danger-500/10 dark:text-danger-500',
            )}
          >
            {hasilUji.berhasil ? (
              <CheckCircle2 className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            ) : (
              <AlertTriangle className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
            )}
            <span>
              {!hasilUji.berhasil && hasilUji.tahap === 'sambungan' && (
                <strong className="block">Belum bisa menyambung ke server email.</strong>
              )}
              {!hasilUji.berhasil && hasilUji.tahap === 'pengiriman' && (
                <strong className="block">Sambungan berhasil, pengirimannya yang ditolak.</strong>
              )}
              {hasilUji.pesan}
            </span>
          </div>
        )}
      </div>
    </Bagian>
  );
};

// -- Pembayaran -----------------------------------------------------------

const BagianDuitku = ({ data, bolehUbah, onSimpan, menyimpan }) => {
  const [f, setF] = useState({
    enabled: Boolean(data.enabled),
    isProduction: Boolean(data.isProduction),
    merchantCode: data.merchantCode ?? '',
    apiKey: '',
  });

  const aktif = data.siap && data.enabled;

  return (
    <Bagian
      icon={CreditCard}
      judul="Pembayaran (Duitku)"
      deskripsi="Selama belum aktif, pembelian tetap bisa dibuat tetapi harus Anda tandai lunas sendiri dari halaman Transaksi."
      badge={
        <Badge tone={aktif ? (data.isProduction ? 'success' : 'info') : 'warning'} size="sm">
          {aktif ? (data.isProduction ? 'Produksi' : 'Sandbox') : 'Manual'}
        </Badge>
      }
      kunciRoot
      bolehUbah={bolehUbah}
    >
      <Toggle
        checked={f.enabled}
        onChange={(v) => setF((s) => ({ ...s, enabled: v }))}
        disabled={!bolehUbah}
        label="Nyalakan pembayaran online"
        description="Kalau dimatikan, semua pembelian masuk ke jalur manual dan tidak ada permintaan yang dikirim ke Duitku."
      />
      <Toggle
        checked={f.isProduction}
        onChange={(v) => setF((s) => ({ ...s, isProduction: v }))}
        disabled={!bolehUbah}
        label="Mode produksi"
        description="Matikan selama masih menguji. Sandbox dan produksi memakai alamat serta kredensial yang berbeda, jadi kunci yang satu tidak akan bekerja di yang lain."
      />

      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Kode merchant" hint="Ada di dashboard Duitku, bagian Proyek">
          {(p) => (
            <Input
              {...p}
              value={f.merchantCode}
              onChange={(e) => setF((s) => ({ ...s, merchantCode: e.target.value }))}
              disabled={!bolehUbah}
            />
          )}
        </Field>
        <Field
          label={
            <span className="flex flex-wrap items-center gap-2">
              Kunci API <Jejak terisi={data.apiKeyTerisi} jejak={data.apiKeyJejak} />
            </span>
          }
          hint="Biarkan kosong kalau tidak ingin menggantinya."
        >
          {(p) => (
            <Input
              {...p}
              type="password"
              autoComplete="off"
              placeholder={data.apiKeyTerisi ? 'Tersimpan, biarkan kosong' : 'Belum diisi'}
              value={f.apiKey}
              onChange={(e) => setF((s) => ({ ...s, apiKey: e.target.value }))}
              disabled={!bolehUbah}
            />
          )}
        </Field>
      </div>

      <p className="rounded-lg bg-[var(--surface-sunken)] p-3 text-xs break-all text-[var(--text-secondary)]">
        Alamat callback yang perlu didaftarkan di dashboard Duitku:{' '}
        <code className="font-mono">{window.location.origin}/api/v1/webhooks/duitku</code>
      </p>

      <Button
        onClick={() => onSimpan('duitku', f)}
        loading={menyimpan}
        disabled={!bolehUbah}
        icon={Save}
      >
        Simpan pengaturan pembayaran
      </Button>
    </Bagian>
  );
};

// -- Penyedia AI bawaan ---------------------------------------------------

const BagianAi = ({ data, onSimpan, menyimpan }) => {
  const [f, setF] = useState({
    bentuk: data.bentuk ?? 'anthropic',
    baseUrl: data.baseUrl ?? '',
    model: data.model ?? '',
  });

  return (
    <Bagian
      icon={BrainCircuit}
      judul="Penyedia AI bawaan"
      deskripsi="Dipakai untuk pengguna yang belum mengisi alamat dan model sendiri di Pengaturan > AI."
    >
      <p className="flex items-start gap-2 rounded-lg bg-[var(--surface-sunken)] p-3 text-xs text-[var(--text-secondary)]">
        <ShieldCheck className="mt-0.5 size-3.5 shrink-0" aria-hidden="true" />
        Yang bawaan hanya alamat dan nama modelnya. Kunci API tetap milik masing-masing pengguna,
        jadi biaya pemakaian AI tidak pernah jatuh ke Anda.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <Field label="Bentuk API" hint="Menentukan susunan permintaannya">
          {(p) => (
            <Select
              {...p}
              value={f.bentuk}
              onChange={(e) => setF((s) => ({ ...s, bentuk: e.target.value }))}
            >
              <option value="anthropic">Anthropic</option>
              <option value="openai">OpenAI dan yang sejenis</option>
            </Select>
          )}
        </Field>
        <Field label="Alamat API" hint="Kosongkan untuk memakai alamat resmi penyedianya">
          {(p) => (
            <Input
              {...p}
              placeholder="https://api.anthropic.com/v1/messages"
              value={f.baseUrl}
              onChange={(e) => setF((s) => ({ ...s, baseUrl: e.target.value }))}
            />
          )}
        </Field>
        <Field label="Model" hint="Nama model persis seperti di dokumentasi penyedianya">
          {(p) => (
            <Input
              {...p}
              placeholder="claude-sonnet-5"
              value={f.model}
              onChange={(e) => setF((s) => ({ ...s, model: e.target.value }))}
            />
          )}
        </Field>
      </div>

      <Button onClick={() => onSimpan('ai', f)} loading={menyimpan} icon={Save}>
        Simpan penyedia bawaan
      </Button>
    </Bagian>
  );
};

// -- Halaman --------------------------------------------------------------

const SettingsSitePage = () => {
  const toast = useToast();
  const qc = useQueryClient();
  const { user } = useAuthStore();
  const bolehUbahRahasia = user?.platformRole === 'root';

  const { data, isLoading } = useQuery({
    queryKey: ['platform', 'settings'],
    queryFn: () => api.get('/platform/settings'),
  });

  const simpan = useMutation({
    mutationFn: ({ kunci, body }) => api.patch(`/platform/settings/${kunci}`, body),
    onSuccess: () => {
      toast.success('Tersimpan', { description: 'Perubahannya langsung berlaku, tanpa restart.' });
      qc.invalidateQueries({ queryKey: ['platform', 'settings'] });
    },
    onError: (err) =>
      toast.error('Gagal menyimpan', {
        description: err instanceof ApiError ? err.message : 'Coba lagi.',
      }),
  });

  if (isLoading) return <PageLoader label="Memuat pengaturan..." />;

  const p = data?.data;
  if (!p) return null;

  const kirim = (kunci, body) => simpan.mutate({ kunci, body });

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-[var(--text-primary)]">Pengaturan Situs</h1>
        <p className="mt-1 max-w-3xl text-sm text-[var(--text-secondary)]">
          Semua yang dulu hanya bisa diubah lewat berkas <code className="font-mono">.env</code> di
          server. Yang Anda simpan di sini menimpa isi berkas itu dan langsung berlaku, jadi apa
          yang tampak di layar memang yang sedang dipakai.
        </p>
      </div>

      {/* Kunci memuat nilainya supaya formulir menyegarkan isinya sesudah
          disimpan, termasuk penanda "terisi" pada ruas rahasia. */}
      <BagianSmtp
        key={`smtp-${p.smtp.host}-${p.smtp.notifikasiKe}-${p.smtp.passTerisi}-${p.smtp.terbukti}`}
        data={p.smtp}
        bolehUbah={bolehUbahRahasia}
        onSimpan={kirim}
        menyimpan={simpan.isPending}
      />
      <BagianDuitku
        key={`duitku-${p.duitku.merchantCode}-${p.duitku.apiKeyTerisi}-${p.duitku.enabled}`}
        data={p.duitku}
        bolehUbah={bolehUbahRahasia}
        onSimpan={kirim}
        menyimpan={simpan.isPending}
      />
      <BagianAi
        key={`ai-${p.ai.bentuk}-${p.ai.model}`}
        data={p.ai}
        onSimpan={kirim}
        menyimpan={simpan.isPending}
      />
    </div>
  );
};

export default SettingsSitePage;
